import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Save } from "lucide-react";
import { toast } from "sonner";
import { useEventMeta } from "@/lib/event-meta";
import { eventNavLabel } from "@/lib/event-display";
import StatusSemanticNote from "@/components/StatusSemanticNote";
import { API_BASE } from "@/lib/utils";
import { getAuthHeaders } from "@/lib/auth-fetch";
import { resolveMediaUrl } from "@/lib/media-api";
import { useAdminEvents } from "@/contexts/AdminEventsContext";
import WorkspaceMediaField from "@/components/WorkspaceMediaField";
import { EVENT_STATUS_OPTIONS } from "@/lib/event-status";
import { normalizeEventDates } from "@/lib/event-dates";

// const API_BASE = typeof window !== "undefined" ? "" : "http://localhost:8001";

export default function EventBasicsManager() {
  const { id } = useParams();
  const { refreshEvents } = useAdminEvents();
  const eventMeta = useEventMeta(id);
  const resolvedEventId = String(id || "").startsWith("evt_") ? id : eventMeta.id;
  const [value, setValue] = useState({
    handle: "",
    title: "",
    subtitle: "",
    format: "",
    venue: "",
    timezone: "",
    startDate: "",
    endDate: "",
    website: "",
    slug: "",
    status: "",
    publishAt: "",
    accent: "",
    cover: "",
    logo: "",
    customDomain: "",
    theme: "",
    isFree: false,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [orgBrand, setOrgBrand] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/admin/workspace/brand`, { headers: getAuthHeaders() });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) {
          const entitled = Boolean(data.canEditWhitelabel || data.whitelabelEntitled);
          const b = data.branding || null;
          setOrgBrand(entitled && b?.enabled ? b : null);
        }
      } catch {
        if (!cancelled) setOrgBrand(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const applyOrgBrandDefaults = () => {
    if (!orgBrand?.enabled) {
      toast.message("Workspace white-label not available", {
        description: "Enterprise plan required for organization brand defaults.",
        action: { label: "Billing", onClick: () => window.location.assign("/admin/billing") },
      });
      return;
    }
    const accent = orgBrand.colors?.accent || orgBrand.colors?.primary || "";
    setValue((prev) => ({
      ...prev,
      accent: accent || prev.accent,
      logo: orgBrand.logoUrl || prev.logo,
    }));
    toast.success("Applied organization brand defaults");
  };

  useEffect(() => {
    if (!resolvedEventId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/admin/events/${resolvedEventId}/overview`);
        if (!res.ok) throw new Error(`Failed to load event (${res.status})`);
        const payload = await res.json();
        const e = payload?.event || {};
        if (!cancelled) {
          setValue({
            handle: e.handle || "",
            title: e.title || "",
            subtitle: e.subtitle || "",
            format: e.format || "",
            venue: e.venue || "",
            timezone: e.timezone || "",
            startDate: e.startDate || "",
            endDate: e.endDate || "",
            website: e.website || "",
            slug: e.slug || "",
            status: e.status || "",
            publishAt: e.publishAt || "",
            accent: e.accent || "",
            cover: e.cover || "",
            logo: e.logo || "",
            customDomain: e.customDomain || "",
            theme: e.theme || "",
            isFree: Boolean(e.isFree),
          });
          setError("");
        }
      } catch (err) {
        if (!cancelled) setError(err?.message || "Failed to load event");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [resolvedEventId]);

  const save = async () => {
    if (!resolvedEventId) {
      toast.error("Event is still loading. Please try again.");
      return;
    }
    try {
      setSaving(true);
      const { startDate, endDate } = normalizeEventDates(value.startDate, value.endDate);
      const res = await fetch(`${API_BASE}/api/admin/events/${resolvedEventId}`, {
        method: "PUT",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ ...value, startDate, endDate }),
      });
      if (res.status === 409) {
        const body = await res.json().catch(() => ({}));
        const detail = typeof body?.detail === "string" ? body.detail : "This public URL slug is already in use.";
        throw new Error(detail);
      }
      if (!res.ok) throw new Error(`Failed to save event basics (${res.status})`);
      await refreshEvents();
      toast.success("Event basics saved", {
        description: value.cover
          ? "Cover saved for portfolio cards and landing previews."
          : "Add an event cover here for portfolio thumbnails (Page Builder hero is separate).",
      });
      setError("");
    } catch (err) {
      setError(err?.message || "Failed to save");
      toast.error("Could not save event basics");
    } finally {
      setSaving(false);
    }
  };

  const saveDisabled = saving || loading || !resolvedEventId;
  const saveButton = (
    <button className="btn-primary shrink-0" type="button" onClick={save} disabled={saveDisabled} data-testid="event-basics-save">
      <Save size={14} /> {saving ? "Saving..." : "Save"}
    </button>
  );

  return (
    <div className="p-8 lg:p-12 max-w-[1200px] mx-auto" data-testid="event-basics-manager">
      <div className="sticky top-0 z-20 -mx-8 lg:-mx-12 px-8 lg:px-12 py-4 mb-6 bg-[color-mix(in_srgb,var(--bg-base)_94%,transparent)] backdrop-blur-md border-b border-[var(--border-default)]">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="text-overline">{eventMeta.title} · event basics</div>
            <h1 className="font-heading text-4xl mt-2 tracking-tight font-medium">Event basics</h1>
            <p className="text-sm text-[var(--text-secondary)] mt-2">Update location, dates, website, and core metadata used across modules.</p>
          </div>
          {saveButton}
        </div>
      </div>

      {loading ? <StatusSemanticNote state="loading" message="Loading event basics. Fields will unlock when data is ready." /> : null}

      <div className="mt-8 surface p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field
          label="Sidebar handle"
          hint="Short name shown in the left nav. Leave blank to use the event title. Does not change the internal event ID."
          value={value.handle}
          onChange={(next) => setValue((prev) => ({ ...prev, handle: next }))}
        />
        <div>
          <span className="text-overline">Internal event ID</span>
          <div className="mt-2 px-3 py-2.5 border border-[var(--border-default)] bg-[var(--bg-elev)] font-mono text-xs text-[var(--text-secondary)]">
            {resolvedEventId || "—"}
          </div>
          <p className="text-[10px] font-mono text-[var(--text-tertiary)] mt-1">Used in URLs and API — never changes.</p>
        </div>
        <Field label="Title" value={value.title} onChange={(next) => setValue((prev) => ({ ...prev, title: next }))} />
        <Field label="Subtitle" value={value.subtitle} onChange={(next) => setValue((prev) => ({ ...prev, subtitle: next }))} />
        <StatusField
          value={value.status}
          publishAt={value.publishAt}
          onChange={(next) => setValue((prev) => ({ ...prev, status: next }))}
          onPublishAtChange={(next) => setValue((prev) => ({ ...prev, publishAt: next }))}
        />
        <ThemeField value={value.theme} onChange={(next) => setValue((prev) => ({ ...prev, theme: next }))} />
        <Field label="Format" value={value.format} onChange={(next) => setValue((prev) => ({ ...prev, format: next }))} />
        <Field label="Venue / location" value={value.venue} onChange={(next) => setValue((prev) => ({ ...prev, venue: next }))} />
        <Field label="Timezone" value={value.timezone} onChange={(next) => setValue((prev) => ({ ...prev, timezone: next }))} />
        <Field label="Start date (YYYY-MM-DD)" value={value.startDate} onChange={(next) => setValue((prev) => ({ ...prev, startDate: next }))} />
        <Field label="End date (YYYY-MM-DD, optional)" hint="Leave blank for a single-day event." value={value.endDate} onChange={(next) => setValue((prev) => ({ ...prev, endDate: next }))} />
        <Field label="Website URL" value={value.website} onChange={(next) => setValue((prev) => ({ ...prev, website: next }))} />
        <Field label="Slug" value={value.slug} onChange={(next) => setValue((prev) => ({ ...prev, slug: next }))} />
        <div className="md:col-span-2">
          <span className="text-overline">Organization brand defaults</span>
          <div className="mt-2 flex flex-wrap items-center gap-3">
            <button type="button" className="btn-secondary text-xs" onClick={applyOrgBrandDefaults} disabled={loading}>
              Apply org accent + logo
            </button>
            <span className="text-[10px] font-mono text-[var(--text-tertiary)]">
              Prefills accent and logo from Settings → White-label. New events can inherit these automatically when enabled there.
            </span>
          </div>
        </div>
        <Field label="Brand accent" value={value.accent} onChange={(next) => setValue((prev) => ({ ...prev, accent: next }))} />
        <WorkspaceMediaField
          label="Event cover"
          hint="Used on landing pages and previews. Pick from the workspace library to avoid huge inline files."
          value={value.cover}
          onChange={(next) => setValue((prev) => ({ ...prev, cover: next }))}
          testId="event-cover-field"
        />
        <WorkspaceMediaField
          label="Event logo"
          hint="Replaces AivoryEMS branding on the public landing. Stored as a hosted URL."
          value={value.logo}
          onChange={(next) => setValue((prev) => ({ ...prev, logo: next }))}
          testId="event-logo-field"
        />
        <CustomDomainField
          eventId={resolvedEventId}
          value={value.customDomain}
          onChange={(next) => setValue((prev) => ({ ...prev, customDomain: next }))}
        />
        <FreeEventField value={value.isFree} onChange={(next) => setValue((prev) => ({ ...prev, isFree: next }))} />
      </div>

      <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-[var(--border-default)] pt-6">
        <p className="text-xs text-[var(--text-tertiary)]">Save after editing — custom domain changes must be saved before Provision SSL.</p>
        {saveButton}
      </div>

      {!loading && !saving ? (
        <StatusSemanticNote state="readonly" message="Editing updates event metadata used across Page Builder, landing pages, and reports." />
      ) : null}
      {error ? <StatusSemanticNote state="error" message={error} /> : null}
    </div>
  );
}

function Field({ label, value, onChange, hint }) {
  return (
    <label className="block">
      <span className="text-overline">{label}</span>
      <input className="input-dark mt-2" value={value || ""} onChange={(e) => onChange(e.target.value)} />
      {hint ? <span className="text-xs text-[var(--text-tertiary)] mt-2 block font-mono">{hint}</span> : null}
    </label>
  );
}

// Status controls whether the public landing and registration form are available.
function StatusField({ value, publishAt, onChange, onPublishAtChange }) {
  const current = EVENT_STATUS_OPTIONS.includes(value) ? value : "Draft";
  const showPublishAt = current === "Scheduled";
  return (
    <div className="block md:col-span-2">
      <label className="block">
        <span className="text-overline">Status</span>
        <select
          className="input-dark mt-2 w-full"
          value={current}
          onChange={(e) => onChange(e.target.value)}
          data-testid="event-basics-status"
        >
          {EVENT_STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </label>
      {showPublishAt ? (
        <label className="block mt-4">
          <span className="text-overline">Publish date & time</span>
          <input
            type="datetime-local"
            className="input-dark mt-2 w-full"
            value={toDatetimeLocalValue(publishAt)}
            onChange={(e) => onPublishAtChange(fromDatetimeLocalValue(e.target.value))}
            data-testid="event-basics-publish-at"
          />
          <span className="text-[10px] font-mono text-[var(--text-tertiary)] mt-1 block">
            The public site and registration open after this time.
          </span>
        </label>
      ) : null}
      <ul className="mt-4 space-y-1 text-[10px] font-mono text-[var(--text-tertiary)]">
        <li>Draft — hidden from the public site and registration.</li>
        <li>Scheduled — opens after the publish date above.</li>
        <li>Selling — public site and registration are open until the event ends.</li>
        <li>Live — public site during the event dates; registration closed.</li>
        <li>Completed — archive site only; registration closed.</li>
      </ul>
    </div>
  );
}

function toDatetimeLocalValue(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const d = new Date(raw.includes("T") ? raw : `${raw}T00:00:00`);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromDatetimeLocalValue(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString();
}

// White-label domain for this event. When the operator points an external
// domain (DNS A-record) at the server and pastes it here, the SPA detects
// `window.location.hostname` matching this value and serves the event's
// landing at the root path -- visitors never see /e/<slug>. The Provision
// SSL button below writes a Caddyfile snippet on the server and triggers a
// reload; Caddy auto-issues a Let's Encrypt cert in the background. Full
// architecture + runbook in infra/CUSTOM_DOMAINS.md.
function CustomDomainField({ value, onChange, eventId }) {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState("");
  // Track the value at the time of last fetch -- if the operator types a
  // new domain into the input, they must Save (which PUTs to the basics
  // endpoint) before Provision can act on the new value.
  const savedDomain = String(status?.domain || "");
  const editedDomain = String(value || "").trim().toLowerCase().replace(/^https?:\/\//, "").split("/")[0].replace(/^www\./, "");
  const dirty = editedDomain !== savedDomain && editedDomain.length > 0;

  const refreshStatus = useCallback(async () => {
    if (!eventId) return;
    try {
      const res = await fetch(`${API_BASE}/api/admin/events/${eventId}/custom-domain/status`);
      if (res.ok) setStatus(await res.json());
    } catch {
      /* leave status as-is */
    }
  }, [eventId]);

  useEffect(() => {
    refreshStatus();
  }, [refreshStatus]);

  const provision = async () => {
    if (!eventId) return;
    if (dirty) {
      toast.error("Save the event first, then click Provision SSL");
      return;
    }
    if (!editedDomain) {
      toast.error("Enter a domain first");
      return;
    }
    setBusy("provisioning");
    try {
      const res = await fetch(`${API_BASE}/api/admin/events/${eventId}/custom-domain/provision`, {
        method: "POST",
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data?.ok) {
        toast.success("Caddy reloaded. SSL certificate is being issued in the background.");
        await refreshStatus();
        // Cert issuance is async (Let's Encrypt HTTP-01 typically 5-30s). Re-poll.
        setTimeout(refreshStatus, 10000);
      } else {
        toast.error(data?.error || data?.detail || `Provisioning failed (${res.status})`);
      }
    } catch (err) {
      toast.error(err?.message || "Provisioning network error");
    } finally {
      setBusy("");
    }
  };

  const remove = async () => {
    if (!eventId || !savedDomain) return;
    if (typeof window !== "undefined" && !window.confirm(`Remove SSL provisioning for ${savedDomain}? Visitors hitting this domain will get a cert error until you re-provision.`)) return;
    setBusy("removing");
    try {
      const res = await fetch(`${API_BASE}/api/admin/events/${eventId}/custom-domain/provision`, {
        method: "DELETE",
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        toast.success("Removed.");
        await refreshStatus();
      } else {
        toast.error(data?.detail || `Remove failed (${res.status})`);
      }
    } catch (err) {
      toast.error(err?.message || "Remove network error");
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="block md:col-span-2">
      <span className="text-overline">Custom domain (white-label public landing)</span>
      <div className="mt-2 flex flex-wrap gap-2 items-stretch">
        <input
          className="input-dark flex-1 min-w-[260px]"
          placeholder="e.g. events.example.com"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          data-testid="event-basics-custom-domain"
        />
        <button
          type="button"
          className="btn-primary"
          onClick={provision}
          disabled={!eventId || busy === "provisioning" || !editedDomain || dirty}
          data-testid="event-basics-custom-domain-provision"
          title={dirty ? "Save the event first" : ""}
        >
          {busy === "provisioning" ? "Provisioning…" : "Provision SSL"}
        </button>
        {status?.configured ? (
          <button
            type="button"
            className="btn-ghost border border-[var(--border-default)]"
            onClick={remove}
            disabled={busy === "removing"}
            data-testid="event-basics-custom-domain-remove"
          >
            {busy === "removing" ? "Removing…" : "Remove"}
          </button>
        ) : null}
      </div>
      <CustomDomainStatusPills status={status} dirty={dirty} editedDomain={editedDomain} />
      <span className="text-[10px] font-mono text-[var(--text-tertiary)] mt-2 block">
        Hostname only (e.g. events.example.com — no https://, no path). DNS A-record for this hostname must point to the server before Provision SSL — Let's Encrypt validates over HTTP-01 and will fail otherwise. Cert renews automatically.
      </span>
    </div>
  );
}

// Visual feedback for the operator: DNS resolves / domain configured /
// Caddy reachable. Each pill is independently colored so the operator can
// see at a glance which step is failing.
function CustomDomainStatusPills({ status, dirty, editedDomain }) {
  if (!status) return null;
  if (!editedDomain) return null;
  const dnsOk = status?.dns?.match;
  const configured = Boolean(status?.configured);
  const caddyOk = status?.caddy_reachable;
  const Pill = ({ label, state }) => {
    const palette = {
      ok: "bg-[#34D399]/15 text-[#34D399] border-[#34D399]/40",
      warn: "bg-[#FBBF24]/15 text-[#FBBF24] border-[#FBBF24]/40",
      bad: "bg-[#F87171]/15 text-[#F87171] border-[#F87171]/40",
      neutral: "bg-[var(--bg-elev)] text-[var(--text-secondary)] border-[var(--border-default)]",
    };
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest border ${palette[state] || palette.neutral}`}>
        {label}
      </span>
    );
  };
  return (
    <div className="mt-2 flex flex-wrap gap-2 items-center">
      <Pill label={dnsOk ? "DNS OK" : "DNS not pointing here"} state={dnsOk ? "ok" : "bad"} />
      <Pill label={configured ? "Caddy block written" : "Not provisioned"} state={configured ? "ok" : "neutral"} />
      <Pill
        label={caddyOk === true ? "Caddy reachable" : caddyOk === false ? "Caddy unreachable" : "Caddy unknown"}
        state={caddyOk === true ? "ok" : caddyOk === false ? "bad" : "neutral"}
      />
      {dirty ? (
        <Pill label="Unsaved domain edit" state="warn" />
      ) : null}
    </div>
  );
}

// Toggles the free-event mode. When on, the public /e/<slug>/register page
// skips the ticket selection and payment steps and lands the visitor on the
// "Tell us about you" form directly. Existing ticket configuration is left
// untouched -- flipping back recovers it.
function FreeEventField({ value, onChange }) {
  return (
    <label className="block md:col-span-2">
      <span className="text-overline">Free event</span>
      <div className="mt-2 flex items-center gap-3">
        <button
          type="button"
          role="switch"
          aria-checked={Boolean(value)}
          onClick={() => onChange(!value)}
          className={`relative inline-flex h-6 w-11 items-center transition border ${
            value ? "bg-[var(--primary)] border-[var(--primary)]" : "bg-[var(--bg-base)] border-[var(--border-default)]"
          }`}
          data-testid="event-basics-isfree"
        >
          <span
            className={`inline-block h-5 w-5 bg-white transform transition ${
              value ? "translate-x-5" : "translate-x-0.5"
            }`}
          />
        </button>
        <span className="text-sm text-[var(--text-secondary)]">
          {value ? "On — public register page skips ticket + payment steps" : "Off — paid event flow"}
        </span>
      </div>
      <span className="text-[10px] font-mono text-[var(--text-tertiary)] mt-2 block">
        Enable when the event has no ticket fee. Visitors land directly on the registration form;
        existing ticket configuration is preserved on the admin Tickets page but ignored on the
        public flow until you turn this off.
      </span>
    </label>
  );
}

// Per-event theme. Public visitors to /e/<slug> see this theme. Empty
// (or any unrecognized value) falls back to whatever the workspace
// default is set to under Admin -> Settings -> Organization.
function ThemeField({ value, onChange }) {
  const options = [
    { value: "", label: "Workspace default" },
    { value: "dark", label: "Dark" },
    { value: "light", label: "Light" },
  ];
  return (
    <label className="block">
      <span className="text-overline">Public landing theme</span>
      <select
        className="input-dark mt-2 w-full"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid="event-basics-theme"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
      <span className="text-[10px] font-mono text-[var(--text-tertiary)] mt-1 block">
        Applies only to the public /e/&lt;slug&gt; landing for this event.
      </span>
    </label>
  );
}
